import { type IContractBook, MaxUint256, type SupportedChainId, contractBook } from '@eulerxyz/core-sdk'
import type { ContractFunctionConfig } from '@eulerxyz/evc-sdk'
import type { Address } from 'viem'
import { BaseTransactionFactory } from './BaseTransactionFactory'

// Permit2 transferFrom ABI for SkimIn
const permit2TransferFromAbi = [
  {
    inputs: [
      { internalType: 'address', name: 'from', type: 'address' },
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint160', name: 'amount', type: 'uint160' },
      { internalType: 'address', name: 'token', type: 'address' },
    ],
    name: 'transferFrom',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const
import {
  type BorrowTransactionItem,
  ConvertFeesTransactionItem,
  type DepositTransactionItem,
  type DisableBalanceForwarderTransactionItem,
  type EnableBalanceForwarderTransactionItem,
  type RedeemTransactionItem,
  type RepayTransactionItem,
  type RepayWithSharesTransactionItem,
  type SkimTransactionItem,
  type SkimInTransactionItem,
  type TransferTransactionItem,
  type VaultTransactionItem,
  type WithdrawTransactionItem,
  isBorrowTransactionItem,
  isConvertFeesTransactionItem,
  isDepositTransactionItem,
  isDisableBalanceForwarderTransactionItem,
  isEnableBalanceForwarderTransactionItem,
  isRedeemTransactionItem,
  isRepayTransactionItem,
  isRepayWithSharesTransactionItem,
  isSkimTransactionItem,
  isSkimInTransactionItem,
  isTransferTransactionItem,
  isWithdrawTransactionItem,
} from './types'

export class VaultTransactionFactory extends BaseTransactionFactory {
  evcAddress: Address

  constructor(chainId: SupportedChainId, contractBook: IContractBook) {
    super(chainId, contractBook)
    /// todo evc address may have to be passed in specific to the vault
    this.evcAddress = contractBook.evc.address[chainId]
  }
  typeGuardConvertMultiple(transactions: VaultTransactionItem[]): ContractFunctionConfig[] {
    return this.#typeGuardConvertMultiple(transactions)
  }

  #typeGuardConvertMultiple(transactions: VaultTransactionItem[]) {
    const contractFunctionConfigs: ContractFunctionConfig[] = []
    for (const tx of transactions) {
      const call = this.typeGuardConvertSingle(tx)
      if (Array.isArray(call)) {
        contractFunctionConfigs.push(...call)
      } else {
        contractFunctionConfigs.push(call)
      }
    }
    return contractFunctionConfigs
  }

  // @ts-ignore - Override type mismatch
  typeGuardConvertSingle(_transaction: VaultTransactionItem): ContractFunctionConfig | ContractFunctionConfig[] {
    if (isDepositTransactionItem(_transaction)) {
      return this.convertDeposit(_transaction)
    }

    if (isWithdrawTransactionItem(_transaction)) {
      return this.convertWithdraw(_transaction)
    }

    if (isSkimInTransactionItem(_transaction)) {
      return this.convertSkimIn(_transaction)
    }

    if (isSkimTransactionItem(_transaction)) {
      return this.convertSkim(_transaction)
    }

    if (isBorrowTransactionItem(_transaction)) {
      return this.convertBorrow(_transaction)
    }

    if (isRepayTransactionItem(_transaction)) {
      return this.convertRepay(_transaction)
    }

    if (isRepayWithSharesTransactionItem(_transaction)) {
      return this.convertRepayWithShares(_transaction)
    }

    if (isTransferTransactionItem(_transaction)) {
      return this.convertTransfer(_transaction)
    }

    if (isRedeemTransactionItem(_transaction)) {
      return this.convertRedeem(_transaction)
    }

    if (isEnableBalanceForwarderTransactionItem(_transaction)) {
      return this.convertEnableBalanceForwarder(_transaction)
    }

    if (isDisableBalanceForwarderTransactionItem(_transaction)) {
      return this.convertDisableBalanceForwarder(_transaction)
    }

    if (isConvertFeesTransactionItem(_transaction)) {
      return this.convertFees(_transaction)
    }

    throw this.errorUnsupportedTransactionType(_transaction)
  }

  convertDeposit(
    transaction: DepositTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.eVault.abi, 'deposit'> {
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'deposit',
      args: [BigInt(transaction.value), transaction.to],
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }
  }

  convertWithdraw(
    transaction: WithdrawTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.eVault.abi, 'withdraw' | 'redeem'> {
    const maxWithCompatibility = transaction.userShares ? BigInt(transaction.userShares) : MaxUint256
    const assetAmountBigInt = transaction.isMax ? maxWithCompatibility : BigInt(transaction.value)
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: transaction.isMax ? 'redeem' : 'withdraw',
      args: [assetAmountBigInt, transaction.to, transaction.from],
      onBehalfOfAccount: transaction?.onBehalfOf || transaction.from,
      value: 0n,
    }
  }

  convertSkim(transaction: SkimTransactionItem): ContractFunctionConfig<typeof contractBook.eVault.abi, 'skim'> {
    // WARNING! Skim max_uint should generally be avoided, because if assets get
    // skimmed by other account first, the operation will not error out, and simply deposit 0
    // which might not be verified by subsequent check
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'skim',
      args: [BigInt(transaction.value), transaction.to],
      onBehalfOfAccount: transaction?.onBehalfOf || transaction.from,
      value: 0n,
    }
  }

  /**
   * SkimIn: Push tokens from user wallet to vault via Permit2, then skim into shares.
   *
   * Generates two EVC calls:
   * 1. Permit2.transferFrom(owner, vault, amount, token) - push tokens to vault
   * 2. vault.skim(amount, receiver) - convert tokens to shares
   */
  convertSkimIn(transaction: SkimInTransactionItem): ContractFunctionConfig[] {
    const permit2Address = this.contractBook.permit2.address[this.chainId]
    const amount = BigInt(transaction.value)

    // 1. Permit2.transferFrom to push tokens from owner to vault
    const transferFromCall: ContractFunctionConfig<typeof permit2TransferFromAbi, 'transferFrom'> = {
      address: permit2Address,
      abi: permit2TransferFromAbi,
      functionName: 'transferFrom',
      args: [
        transaction.owner, // from: owner's wallet
        transaction.vault, // to: vault
        amount > BigInt(2 ** 160 - 1) ? BigInt(2 ** 160 - 1) : amount, // amount (uint160 max)
        transaction.asset.address, // token
      ],
      onBehalfOfAccount: transaction.from, // Called on behalf of sub-account
      value: 0n,
    }

    // 2. vault.skim to convert tokens to shares
    const skimCall: ContractFunctionConfig<typeof contractBook.eVault.abi, 'skim'> = {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'skim',
      args: [amount, transaction.to],
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }

    return [transferFromCall, skimCall]
  }

  convertBorrow(transaction: BorrowTransactionItem): ContractFunctionConfig<typeof contractBook.eVault.abi, 'borrow'> {
    const assetAmountBigInt = BigInt(transaction.value)
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'borrow',
      args: [assetAmountBigInt, transaction.to],
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }
  }

  convertRepay(transaction: RepayTransactionItem): ContractFunctionConfig<typeof contractBook.eVault.abi, 'repay'> {
    const assetAmountBigInt = transaction.isMax ? MaxUint256 : BigInt(transaction.value)
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'repay',
      args: [assetAmountBigInt, transaction.to],
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }
  }

  convertRepayWithShares(
    transaction: RepayWithSharesTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.eVault.abi, 'repayWithShares'> {
    const assetAmountBigInt = transaction.isMax ? MaxUint256 : BigInt(transaction.value)
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'repayWithShares',
      args: [assetAmountBigInt, transaction.to],
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }
  }

  convertTransfer(
    transaction: TransferTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.eVault.abi, 'transfer' | 'transferFromMax'> {
    return transaction.isMax
      ? {
          address: transaction.vault,
          abi: contractBook.eVault.abi,
          functionName: 'transferFromMax',
          args: [transaction.from, transaction.to], // from, to
          onBehalfOfAccount: transaction.from,
          value: 0n,
        }
      : {
          address: transaction.vault,
          abi: contractBook.eVault.abi,
          functionName: 'transfer',
          args: [transaction.to, BigInt(transaction.sharesValue)], // to, amount
          onBehalfOfAccount: transaction.from,
          value: 0n,
        }
  }

  convertRedeem(transaction: RedeemTransactionItem): ContractFunctionConfig<typeof contractBook.eVault.abi, 'redeem'> {
    const assetAmountBigInt = BigInt(transaction.value)
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'redeem',
      args: [assetAmountBigInt, transaction.to, transaction.from],
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }
  }

  convertEnableBalanceForwarder(
    transaction: EnableBalanceForwarderTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.eVault.abi, 'enableBalanceForwarder'> {
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'enableBalanceForwarder',
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }
  }

  convertDisableBalanceForwarder(
    transaction: DisableBalanceForwarderTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.eVault.abi, 'disableBalanceForwarder'> {
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'disableBalanceForwarder',
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }
  }

  convertFees(
    transaction: ConvertFeesTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.eVault.abi, 'convertFees'> {
    return {
      address: transaction.vault,
      abi: contractBook.eVault.abi,
      functionName: 'convertFees',
      onBehalfOfAccount: transaction.from,
      value: 0n,
    }
  }
}
